'use client'

import { useFormStore, useStoreState } from '@ariakit/react'
import { addMonths } from 'date-fns'
import { useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import Cookies from 'universal-cookie'

import { Dropdown, Input } from '@syconium/little-miss-figgy'
import { themeColorsLightMode } from '@syconium/little-miss-figgy/dist/themes/default/themeColorsLightMode'
import { SupportedLocaleIdentifier, languageNamesByLocale } from '@syconium/weeping-figs'

import { StyledInterstitial } from '../../../../components/chrome/Interstitial/Interstitial'
import { cookieKeys } from '../../../_config/Cookies.config'
import { useLocalization } from '../../../_providers/LocalizationProvider.client'
import { usePreviewPanel } from '../../../_providers/PreviewPanelProvider.client'
import { NextDialogProvider } from '../NextDialog.client'

import * as Styles from './PreviewPanelDialog.styles'
import { ProductTagInput } from './ProductTagInput'

export const CONTENT_KEY = cookieKeys.contentMode.key
export const PRODUCT_DATA_KEY = cookieKeys.productData.key
const COPY_SUCCESS_TIMEOUT = 1500
const LOCAL_PATH = { path: '/' }

const PreviewPanelContent = () => {
  const [isCopyClicked, setIsCopyClicked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { locale, regions, region, changeLocale } = useLocalization()
  const browserCookies = useMemo(() => new Cookies(), [])

  // FORM STATE
  const defaultValues = useMemo(() => {
    // The content cookie holds either 'preview' or an ISO timestamp string
    // Any truthy value means preview mode is active.
    const contentCookie = browserCookies.get(CONTENT_KEY) ?? ''
    const isTimestamp = !isNaN(new Date(contentCookie).getTime())
    return {
      content: !!contentCookie,
      productData: browserCookies.get(PRODUCT_DATA_KEY) ?? '',
      date: isTimestamp ? contentCookie : '',
      country: region.name,
      language: locale,
    }
  }, [browserCookies, region, locale])
  const form = useFormStore({ defaultValues })
  const values = useStoreState(form).values
  const currentRegion = useMemo(
    () => regions.find(r => r.name === values.country) ?? region,
    [regions, values.country, region]
  )

  // SUBMIT
  form.useSubmit(({ values }) => {
    const syncCookie = (key: string, value?: string | null) => {
      if (value) {
        browserCookies.set(key, value, LOCAL_PATH)
      } else {
        browserCookies.remove(key, LOCAL_PATH)
      }
    }

    const { content, productData, date, country, language } = values
    const localeChanged = defaultValues.country !== country || defaultValues.language !== language

    setIsSubmitting(true)
    syncCookie(CONTENT_KEY, content ? date || 'preview' : null)
    syncCookie(PRODUCT_DATA_KEY, content ? productData : '')

    if (localeChanged) {
      changeLocale(currentRegion, language)
    } else {
      window.location.reload()
    }
  })

  // FUNCTIONS
  const setDate = (date: Date | null) => {
    // Default to 11:55pm so the timestamp falls after scheduled content releases
    if (date?.getHours() === 0) date.setHours(23, 55, 0, 0)
    form.setValue('date', date?.toISOString() ?? '')
  }

  const copyLinkToClipboard = () => {
    const url = new URL(window.location.href)
    if (defaultValues.content) url.searchParams.set('content', defaultValues.date || 'preview')
    if (defaultValues.productData) url.searchParams.set('product-data', defaultValues.productData)

    navigator.clipboard.writeText(url.toString())
    setIsCopyClicked(true)
    setTimeout(() => setIsCopyClicked(false), COPY_SUCCESS_TIMEOUT)
  }

  // DISPLAY STATE
  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(defaultValues),
    [values, defaultValues]
  )
  const isPreviewMode = values.content
  const showCopyButton = (defaultValues.content || !!defaultValues.productData) && !isDirty

  return (
    <>
      <Styles.DialogHeaderRow>
        <Styles.DialogHeader>Preview Settings</Styles.DialogHeader>
        <Styles.HeaderButtonRow>
          {showCopyButton && (
            <div style={{ position: 'relative' }}>
              <Styles.HeaderButton
                onClick={copyLinkToClipboard}
                title='Copy preview link'
                disabled={isCopyClicked}
              >
                <Styles.LinkIcon $active={isCopyClicked} width='25' />
                <Styles.CheckMarkIcon $active={isCopyClicked} width='25' />
              </Styles.HeaderButton>
              <Styles.Tooltip $active={isCopyClicked}>Copied!</Styles.Tooltip>
            </div>
          )}
          <Styles.DialogClose title='close' aria-label='close' />
        </Styles.HeaderButtonRow>
      </Styles.DialogHeaderRow>

      <Styles.HotkeyHelper>
        Press <div>⌥</div>
        <div>⌘</div>
        <div>/</div> to Quick Open
      </Styles.HotkeyHelper>

      <Styles.Settings store={form} resetOnSubmit={false} autoComplete='off'>
        <Styles.PreviewModeSection>
          <Styles.SettingLabel htmlFor='preview-mode'>
            <Styles.PreviewRow>
              <Styles.PreviewIcon $active={isPreviewMode} />
              {`Preview Mode`}
              <Styles.PreviewState $active={isPreviewMode}>
                {isPreviewMode ? 'ON' : 'OFF'}
              </Styles.PreviewState>
            </Styles.PreviewRow>
          </Styles.SettingLabel>
          <Styles.PreviewSwitch
            id='preview-mode'
            checked={values.content}
            onChange={checked => form.setValue('content', checked)}
            aria-label='Preview Mode'
            size='medium'
            $active={isPreviewMode}
          />
        </Styles.PreviewModeSection>

        <Styles.SettingRow>
          <Styles.SettingLabel htmlFor='date'>RELEASE DATE</Styles.SettingLabel>
          <Styles.DatePickerWrap>
            <DatePicker
              id='date'
              name='date'
              selected={values.date ? new Date(values.date) : undefined}
              onChange={setDate}
              dateFormat='MMMM d, yyyy h:mm aa'
              minDate={new Date()}
              placeholderText={isPreviewMode ? 'Select a date' : 'Enable Preview Mode to Edit'}
              showTimeInput
              disabled={!isPreviewMode}
              isClearable
              maxDate={addMonths(new Date(), 4)} // Timeline feature has a 4-month limit
              customInput={<Input />}
            />
            {!values.date ? (
              <Styles.DateIcon width='16' height='16' $disabled={!isPreviewMode} />
            ) : null}
          </Styles.DatePickerWrap>
        </Styles.SettingRow>

        <Styles.SettingRow>
          <Styles.SettingLabel htmlFor='product-tag'>PRODUCT TAGS</Styles.SettingLabel>
          <ProductTagInput
            updateFormValue={form.setValue}
            tags={values.productData}
            disabled={!isPreviewMode}
          />
        </Styles.SettingRow>

        <Styles.LocaleRow>
          <Styles.SettingRow>
            <Styles.SettingLabel htmlFor='country'>COUNTRY/REGION</Styles.SettingLabel>
            <Dropdown
              isScrollable
              onChange={country => {
                const newRegion = regions.find(r => r.name === country) ?? region
                form.setValue('country', country)
                form.setValue('language', newRegion.defaultLocale)
              }}
              options={[
                // USA #1!
                'United States',
                ...regions.filter(r => r.name !== 'United States').map(r => r.name),
              ]}
              placeholderText=''
              selection={values.country}
              hidePlaceholderAsOption
            />
          </Styles.SettingRow>

          <Styles.SettingRow>
            <Styles.SettingLabel htmlFor='language'>LANGUAGE</Styles.SettingLabel>
            <Dropdown
              isScrollable
              onChange={language => form.setValue('language', language)}
              options={Object.values(currentRegion.languages).map(reg => reg.locale)}
              renderOptionText={locale =>
                languageNamesByLocale[locale as SupportedLocaleIdentifier]
              }
              renderSelectionText={locale =>
                languageNamesByLocale[locale as SupportedLocaleIdentifier]
              }
              placeholderText=''
              selection={values.language}
              hidePlaceholderAsOption
            />
          </Styles.SettingRow>
        </Styles.LocaleRow>

        <Styles.SubmitButton
          type='submit'
          disabled={!isDirty}
          size='large'
          callToActionColor={
            isSubmitting
              ? themeColorsLightMode.fill.celebratory.strong
              : themeColorsLightMode.fill.strong
          }
        >
          {isSubmitting ? 'Changes Applied' : 'Apply Changes'}
        </Styles.SubmitButton>
      </Styles.Settings>

      <Styles.SystemInfo>
        <Styles.SettingLabel>SYSTEM INFORMATION</Styles.SettingLabel>
        <Styles.Divider />
        <Styles.SystemInfoRow>
          <div>Build:</div> <div>v{process.env.NEXT_PUBLIC_MAGNOLIA_VERSION}</div>
        </Styles.SystemInfoRow>
        <Styles.SystemInfoRow>
          <div>Environment:</div> <Styles.Environment>{process.env.NODE_ENV}</Styles.Environment>
        </Styles.SystemInfoRow>
        <Styles.SystemInfoRow>
          <div>Locale:</div> <div>{locale}</div>
        </Styles.SystemInfoRow>
      </Styles.SystemInfo>
      {isSubmitting ? <StyledInterstitial /> : null}
    </>
  )
}

export const PreviewPanelDialog = () => {
  const { isPanelOpen, togglePanel } = usePreviewPanel()

  return (
    <NextDialogProvider open={isPanelOpen}>
      <Styles.Dialog
        analyticsTitle='Preview Controls'
        variant='right-drawer'
        onClose={togglePanel}
        content={<Styles.Content />}
        unmountOnHide={true}
      >
        <PreviewPanelContent />
      </Styles.Dialog>
    </NextDialogProvider>
  )
}
